import { G } from '@mobily/ts-belt';
import { Box, Typography } from '@mui/material';
import { useMemo } from 'react';
import {
  ReferenceInput,
  SelectInput,
  SelectInputProps,
  useGetList,
  useTranslate,
} from 'react-admin';
import { Control, Controller, useWatch } from 'react-hook-form';

import { isFullyManagedPlan } from '@Common/types/b2b/plans';
import { B2bCompanyPlan, B2bCompanyPlansQuery } from '@Common/types/bo/b2b/plans';
import { GetInstrumentsResponseItem } from '@Common/types/bo/instruments';
import { B2bInstrumentTypeValues } from '@Common/types/instruments/instrument';

import { B2bCampaignFormValues } from '@sh4res/bo-common/components/B2bCampaigns/types';
import { useResourceBasePath, useWatchObject } from '@sh4res/bo-common/hooks';
import { isBoExternal } from '@sh4res/bo-common/utils/env';

type B2bPlacementSupportSelectInputProps = Omit<
  SelectInputProps,
  'choices' | 'source' | 'filter'
> & {
  filter: B2bCompanyPlansQuery['filter'];
  choiceId: number;
  allocationId: number;
  control: Control;
  'data-testid'?: string;
};

export const B2bPlacementSupportSelectInput = ({
  filter,
  allocationId,
  choiceId,
  control,
  'data-testid': dataTestId,
  ...props
}: B2bPlacementSupportSelectInputProps) => {
  const resourceBasePath = useResourceBasePath();

  const {
    type: campaignType,
    matching_contribution_related_cash_source: matchingContributionRelatedCashSource,
  } = useWatchObject<B2bCampaignFormValues>()([
    'type',
    'matching_contribution_related_cash_source',
  ]);
  const selectedPlan = useWatch({
    name: `allocations.${allocationId}.company_collected_choices.${choiceId}.plan_id`,
  });

  const { data = [] } = useGetList<B2bCompanyPlan>(
    `${resourceBasePath}companies/plans`,
    {
      filter,
      sort: { field: 'created_at', order: 'DESC' },
      pagination: { page: 1, perPage: 100 },
    },
    { enabled: !!selectedPlan && selectedPlan !== 'payout' },
  );

  const planData = useMemo(
    () =>
      data
        .flatMap((plan) =>
          plan.shares_plans.map((sharesPlan) => ({
            ...sharesPlan,
            instruments_cash_sources_libre: plan.instruments_cash_sources_libre,
            is_cash_sources_per_instrument:
              !isFullyManagedPlan(sharesPlan.type) && plan.is_cash_sources_per_instrument,
          })),
        )
        .find((plan) => plan.id === selectedPlan),
    [data, selectedPlan],
  );

  const translate = useTranslate();

  const instruments = useMemo(() => {
    if (!planData) {
      if (selectedPlan === 'ccb') {
        return data.flatMap((plan) => plan.shares_plans.flatMap((sp) => sp.instruments_universe));
      }

      return [];
    }
    if (campaignType === 'withdrawal_at_maturity') {
      return [];
    }
    if (campaignType === 'matching_contribution') {
      return planData.instruments_universe.filter((instrumentId) => {
        const instrumentConfig = planData.instruments_cash_sources_libre.find(
          (entry) => entry.instrument_id === instrumentId,
        );
        if (!instrumentConfig) {
          return false;
        }
        return (
          G.isNotNullable(matchingContributionRelatedCashSource) &&
          instrumentConfig.cash_sources?.includes(matchingContributionRelatedCashSource) &&
          instrumentConfig.is_matching_contributions_enabled
        );
      });
    }
    if (!planData.is_cash_sources_per_instrument) {
      return planData.instruments_universe;
    }
    return planData.instruments_universe.filter(
      (instrumentId) =>
        !!planData.instruments_cash_sources_libre
          .find((entry) => entry.instrument_id === instrumentId)
          ?.cash_sources?.includes(campaignType),
    );
  }, [planData, data, selectedPlan, campaignType, matchingContributionRelatedCashSource]);

  if (selectedPlan === 'payout') {
    return <Box sx={{ display: 'flex', justifyContent: 'center' }}>-</Box>;
  }

  if (!planData && selectedPlan !== 'ccb') {
    return null;
  }

  // TODO: risk_profile removed from MVP, to be brought back later https://sh4res.atlassian.net/browse/BRAV-8365
  if (G.isNotNullable(planData) && isFullyManagedPlan(planData.type)) {
    return (
      <Typography sx={{ textAlign: 'center', paddingTop: '5px' }}>
        {translate('resources.bo_common.b2b.campaigns.sections.allocations.risk_profile_current')}
      </Typography>
    );
  }

  const instrumentsReferenceNameColumn = isBoExternal() ? 'short_name' : 'name';

  const formatInstrumentOptionText = (instrument: GetInstrumentsResponseItem) => {
    const name = isBoExternal() ? instrument.short_name : instrument.name;
    const displayName = name || instrument.symbol;
    return instrument.isin ? `${displayName} - ${instrument.isin}` : displayName;
  };

  const filterInstruments = {
    id: instruments,
    ...(selectedPlan === 'ccb'
      ? { type: 'ccb' }
      : { type: B2bInstrumentTypeValues.filter((type) => type !== 'ccb') }),
  };

  return (
    <Controller
      name={`allocations.${allocationId}.company_collected_choices.${choiceId}.instrument_id`}
      control={control}
      render={({ field }) => (
        <ReferenceInput
          source={`allocations.${allocationId}.company_collected_choices.${choiceId}.instrument_id`}
          reference="instruments"
          filter={filterInstruments}
          sort={{ field: instrumentsReferenceNameColumn, order: 'ASC' }}
          perPage={100}
          data-testid={`${dataTestId}_InstrumentReferenceInput`}>
          <SelectInput
            optionText={formatInstrumentOptionText}
            data-testid={dataTestId}
            {...field}
            {...props}
          />
        </ReferenceInput>
      )}
    />
  );
};
